import { FormattedMessage } from 'react-intl';
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Spin } from 'antd';
import myContext from '@/utils/createContext';
import { cashierValidatePaymentSession } from '@/components/H5PC/service';
import { getApplePaySessionApiVersion } from '@/utils/applePaySessionVersion';

/** 与 card-iframe 一致，用于注册 <apple-pay-button>；仅 Safari 等支持 Apple Pay on Web 的环境需要 */
const APPLE_PAY_SDK_SRC =
  'https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js';
/**
 * 钱包可用性：轮询官方按钮是否被置为 disabled（无钱包等）。
 * 发现 disabled 立即结束；若按钮已挂载且持续无 disabled，满 EARLY_OK 可提前结束；最长不超过 MAX。
 */
const APPLE_PAY_WALLET_CHECK_POLL_MS = 100;
const APPLE_PAY_WALLET_CHECK_EARLY_OK_MS = 800;
const APPLE_PAY_WALLET_CHECK_MAX_MS = 2000;
/** 主脚本或子 chunk 异常时，避免永久卡在 Spin */
const APPLE_PAY_SDK_LOAD_FALLBACK_MS = 4000;

function safeAbort(session) {
  if (!session) return;
  try {
    session.abort();
  } catch (e) {
    // ignore
  }
}

function safeCompletePaymentFailure(session) {
  if (typeof window === 'undefined' || !window.ApplePaySession || !session) return;
  try {
    session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
  } catch (e) {
    // ignore
  }
}

/**
 * 兼容后端返回 string 或已反序列化 object 两种情况。
 * Apple 要求 completeMerchantValidation 接收原始 JS 对象。
 */
function parseMerchantSession(rawSession) {
  if (rawSession == null) return null;
  if (typeof rawSession === 'object') return rawSession;
  try {
    return JSON.parse(rawSession);
  } catch (e) {
    console.error('[CashierApplePayButton] parseMerchantSession failed:', e);
    return null;
  }
}

/**
 * Apple Pay 收银台按钮（需先注入 apple-pay-sdk）。
 * 授权后须由父组件 `onCashierApplePayAuthorized` 在业务结束时调用
 * `session.completePayment(SUCCESS|FAILURE)`；未传或抛错时由本组件兜底 FAILURE。
 */
function CashierApplePayButton(props) {
  const ctx = useContext(myContext);
  const merged = { ...ctx, ...props };
  const cashierKey = merged.cashierKey ?? merged.key;
  const {
    applePayConfigInfoBeanData,
    transactionInfo,
    onCashierApplePayAuthorized,
  } = merged;

  /**
   * 点击与异步回调内始终读最新值，避免把这些变量放进 effect 依赖
   * 导致频繁解绑/重绑，或意外 abort 进行中的会话。
   */
  const applePayConfigRef = useRef(applePayConfigInfoBeanData);
  applePayConfigRef.current = applePayConfigInfoBeanData;

  const onCashierApplePayAuthorizedRef = useRef(onCashierApplePayAuthorized);
  onCashierApplePayAuthorizedRef.current = onCashierApplePayAuthorized;

  /** 金额/币种放 ref：若放进 effect 依赖，父组件轮询或刷新金额会触发 cleanup → abort 会话 */
  const transactionInfoRef = useRef({ totalPrice: '', currencyCode: '' });
  transactionInfoRef.current = {
    totalPrice: transactionInfo?.totalPrice ?? '',
    currencyCode: transactionInfo?.currencyCode ?? '',
  };

  const cashierKeyRef = useRef(cashierKey);
  cashierKeyRef.current = cashierKey;

  /** null：尚未检测；false：Chrome 等无 Apple Pay on Web；true：可展示官方按钮 */
  const [browserSupportsApplePay, setBrowserSupportsApplePay] = useState(null);
  /** 钱包检查结束后：true 展示官方按钮，false 展示不支持 */
  const [showApplePayButton, setShowApplePayButton] = useState(true);
  /** SDK 就绪后、完成 disabled 检测前为 false，此期间用 Spin 盖住按钮避免用户先点到不可用态 */
  const [walletCheckDone, setWalletCheckDone] = useState(false);
  const [applePaySdkReady, setApplePaySdkReady] = useState(false);
  /** SDK 脚本加载失败时置为 true，用于兜底展示不支持提示 */
  const [sdkLoadFailed, setSdkLoadFailed] = useState(false);

  const sessionRef = useRef(null);
  /** 用 ref 直接绑定 <apple-pay-button> DOM，避免 getElementById 时机不可靠 */
  const applePayButtonRef = useRef(null);

  const totalPrice = transactionInfo?.totalPrice;
  const currencyCode = transactionInfo?.currencyCode;
  const merchantIdentifier = applePayConfigInfoBeanData?.merchantIdentifier;

  /** 一次完成：检测 Apple Pay on Web → 按需注入 apple-pay-sdk.js → applePaySdkReady */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const supported = typeof window.ApplePaySession !== 'undefined';
    setBrowserSupportsApplePay(supported);
    if (!supported) return undefined;

    let cancelled = false;
    let safetyId;

    const markSdkReady = () => {
      if (!cancelled) setApplePaySdkReady(true);
    };
    const markSdkFailed = () => {
      if (cancelled) return;
      console.error('[CashierApplePayButton] Apple Pay SDK failed to load');
      setSdkLoadFailed(true);
      setApplePaySdkReady(true); // 结束 Spin，由 renderBody 根据 sdkLoadFailed 展示不支持
    };
    const armSafetyNet = () => {
      if (safetyId) window.clearTimeout(safetyId);
      safetyId = window.setTimeout(() => {
        if (cancelled) return;
        // 兜底超时：检查 <apple-pay-button> 是否已被 SDK 注册为自定义元素
        const isRegistered = typeof customElements !== 'undefined'
          && customElements.get('apple-pay-button') != null;
        if (isRegistered) {
          markSdkReady();
        } else {
          markSdkFailed();
        }
      }, APPLE_PAY_SDK_LOAD_FALLBACK_MS);
    };

    const existing = document.querySelector(`script[src="${APPLE_PAY_SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', markSdkReady, { once: true });
      existing.addEventListener('error', markSdkFailed, { once: true });
      // 脚本可能已加载完毕，直接触发一次
      setTimeout(markSdkReady, 0);
      armSafetyNet();
    } else {
      try {
        const script = document.createElement('script');
        script.src = APPLE_PAY_SDK_SRC;
        script.async = true;
        script.onload = () => { if (!cancelled) markSdkReady(); };
        script.onerror = () => { if (!cancelled) markSdkFailed(); };
        document.head.appendChild(script);
      } catch (e) {
        console.error('Apple Pay SDK inject error:', e);
        markSdkFailed();
      }
      armSafetyNet();
    }

    return () => {
      cancelled = true;
      if (safetyId) window.clearTimeout(safetyId);
    };
  }, []);

  /** SDK 就绪后轮询：出现 disabled 立即判不可用；按钮已挂载且持续无 disabled 满 EARLY_OK 即结束 */
  useEffect(() => {
    if (!browserSupportsApplePay || !applePaySdkReady) {
      setWalletCheckDone(false);
      return undefined;
    }

    setWalletCheckDone(false);
    setShowApplePayButton(true);

    const start = Date.now();
    let cancelled = false;
    let finished = false;
    let intervalId = null;

    const stop = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const finish = (show) => {
      finished = true;
      setShowApplePayButton(show);
      setWalletCheckDone(true);
      stop();
    };

    const runCheck = () => {
      if (cancelled || finished) return;
      const elapsed = Date.now() - start;
      const appleButton = applePayButtonRef.current;

      if (appleButton?.hasAttribute('disabled')) {
        finish(false);
        return;
      }
      if (appleButton && !appleButton.hasAttribute('disabled') && elapsed >= APPLE_PAY_WALLET_CHECK_EARLY_OK_MS) {
        finish(true);
        return;
      }
      if (elapsed >= APPLE_PAY_WALLET_CHECK_MAX_MS) {
        finish(!!appleButton && !appleButton.hasAttribute('disabled'));
      }
    };

    const bootId = window.setTimeout(() => {
      if (cancelled) return;
      runCheck();
      if (!cancelled && !finished) {
        intervalId = window.setInterval(runCheck, APPLE_PAY_WALLET_CHECK_POLL_MS);
      }
    }, 0);

    return () => {
      cancelled = true;
      finished = true;
      window.clearTimeout(bootId);
      stop();
    };
  }, [browserSupportsApplePay, applePaySdkReady]);

  /**
   * 注册 click handler。
   * 依赖 walletCheckDone + showApplePayButton 确保按钮已渲染且可用后才绑定；
   * 卸载时同步 abort 进行中的会话。
   */
  useEffect(() => {
    const applePayButton = applePayButtonRef.current;

    // 按钮不可用或尚未完成钱包检测，不注册
    if (!applePayButton || !walletCheckDone || !showApplePayButton) {
      return () => {
        safeAbort(sessionRef.current);
        sessionRef.current = null;
      };
    }

    if (
      !window.ApplePaySession ||
      !cashierKeyRef.current ||
      !merchantIdentifier
    ) {
      return undefined;
    }

    const handler = () => {
      const cfg = applePayConfigRef.current;
      const { totalPrice: amount, currencyCode: cur } = transactionInfoRef.current;

      if (!amount || !cur) {
        console.warn('[CashierApplePayButton] missing amount or currencyCode');
        return;
      }

      const paymentRequest = {
        countryCode: cfg?.countryCode,
        currencyCode: cur,
        supportedNetworks: cfg?.supportedNetworks,
        merchantCapabilities: cfg?.merchantCapabilities,
        merchantIdentifier: cfg?.merchantIdentifier,
        total: {
          label: cfg?.merchantNameAbbr || ' ',
          amount,
          type: 'final',
        },
      };

      console.log('new ApplePaySession', getApplePaySessionApiVersion(), paymentRequest);

      if (sessionRef.current) {
        safeAbort(sessionRef.current);
        sessionRef.current = null;
      }

      const session = new ApplePaySession(getApplePaySessionApiVersion(), paymentRequest);
      sessionRef.current = session;

      const keyForValidate = cashierKeyRef.current;

      /** Apple 要求：必须在 begin() 之前注册所有回调 */
      session.onvalidatemerchant = async (event) => {
        console.log('event.validationURL', event.validationURL);
        const verifyUrl = typeof window !== 'undefined' ? window.location.host : '';
        const channelCode = applePayConfigRef.current?.channelCode;
        let res;
        try {
          res = await cashierValidatePaymentSession(keyForValidate, { channelCode, verifyUrl });
          console.log('/payment/validatePaymentSession/{key} res', res);
        } catch (e) {
          console.error('cashierValidatePaymentSession error', e);
          safeAbort(session);
          return;
        }

        // 异步期间会话已被替换，放弃本次
        if (sessionRef.current !== session) {
          safeAbort(session);
          return;
        }

        const rawSession = res?.data?.session;
        const merchantSession = parseMerchantSession(rawSession);

        if (res?.code === 200000 && merchantSession != null) {
          try {
            console.log('completeMerchantValidation', merchantSession);
            session.completeMerchantValidation(merchantSession);
          } catch (e) {
            console.error('completeMerchantValidation error', e);
            safeAbort(session);
          }
        } else {
          safeAbort(session);
        }
      };

      session.onpaymentauthorized = (event) => {
        const paymentData = event?.payment?.token?.paymentData;
        console.log("onpaymentauthorized",paymentData)
        const cb = onCashierApplePayAuthorizedRef.current;
        if (typeof cb === 'function') {
          try {
            cb(paymentData, session);
          } catch (e) {
            console.error(e);
            safeCompletePaymentFailure(session);
          }
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[CashierApplePayButton] onCashierApplePayAuthorized 未传入，已结束为失败。');
          }
          safeCompletePaymentFailure(session);
        }
      };

      session.oncancel = (e) => {
        console.log('ApplePaySession cancel', e);
        if (sessionRef.current === session) {
          sessionRef.current = null;
        }
      };

      session.onerror = (error) => {
        console.log('ApplePaySession error', error);
      };

      session.begin();
    };

    applePayButton.addEventListener('click', handler);

    return () => {
      applePayButton.removeEventListener('click', handler);
      safeAbort(sessionRef.current);
      sessionRef.current = null;
    };
  }, [merchantIdentifier, applePaySdkReady, walletCheckDone, showApplePayButton]);

  const renderBody = () => {
    if (browserSupportsApplePay === null) {
      return <Spin />;
    }
    if (!browserSupportsApplePay) {
      return <FormattedMessage id="ga-checkout.apple-pay.not-support" />;
    }
    if (!applePaySdkReady) {
      return <Spin />;
    }
    if (sdkLoadFailed) {
      return <FormattedMessage id="ga-checkout.apple-pay.not-support" />;
    }
    if (!totalPrice || !currencyCode) {
      return <FormattedMessage id="ga-checkout.apple-pay.params-error" />;
    }
    if (walletCheckDone && !showApplePayButton) {
      return <FormattedMessage id="ga-checkout.apple-pay.not-support" />;
    }
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          minHeight: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!walletCheckDone && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
            }}
          >
            <Spin />
          </div>
        )}
        <apple-pay-button
          ref={applePayButtonRef}
          buttonstyle="black"
          type="plain"
          locale="en-US"
          id="cashier-apple-pay-button"
        />
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '15px',
        background: '#fff',
        minHeight: '70px',
        color: '#333',
        fontSize: '16px',
      }}
    >
      {renderBody()}
    </div>
  );
}

export default CashierApplePayButton;
